/**
 * Calendar utilities and Czech localization
 */

import {
	format,
	parse,
	startOfWeek,
	endOfWeek,
	getDay,
	addDays,
	addWeeks,
	subWeeks,
	startOfDay,
	isSameDay,
} from 'date-fns'
import { cs } from 'date-fns/locale'
import { dateFnsLocalizer } from 'react-big-calendar'

// Czech locale configuration for react-big-calendar
const locales = {
	cs: cs,
}

export const localizer = dateFnsLocalizer({
	format,
	parse,
	startOfWeek: (date: Date) => startOfWeek(date, { weekStartsOn: 1 }), // Monday
	getDay,
	locales,
})

// Czech messages for react-big-calendar
export const czechMessages = {
	allDay: 'Celý den',
	previous: 'Předchozí',
	next: 'Další',
	today: 'Dnes',
	month: 'Měsíc',
	week: 'Týden',
	work_week: 'Pracovní týden',
	day: 'Den',
	agenda: 'Agenda',
	date: 'Datum',
	time: 'Čas',
	event: 'Událost',
	noEventsInRange: 'V tomto období nejsou žádné události.',
	showMore: (count: number) => `+${count} další`,
}

/**
 * Format a date in Czech locale
 */
export function formatDateCzech(date: Date, formatStr: string): string {
	return format(date, formatStr, { locale: cs })
}

/**
 * Get the start and end dates of a week
 */
export function getWeekRange(date: Date): { start: Date; end: Date } {
	const start = startOfWeek(date, { weekStartsOn: 1 }) // Monday
	const end = endOfWeek(date, { weekStartsOn: 1 }) // Sunday
	return { start, end }
}

/**
 * Navigate to the next week
 */
export function getNextWeek(date: Date): Date {
	return addWeeks(date, 1)
}

/**
 * Navigate to the previous week
 */
export function getPreviousWeek(date: Date): Date {
	return subWeeks(date, 1)
}

/**
 * Get today's date at midnight
 */
export function getToday(): Date {
	return startOfDay(new Date())
}

/**
 * Check if two dates are the same day
 */
export function isSameDayCheck(date1: Date, date2: Date): boolean {
	return isSameDay(date1, date2)
}

/**
 * Format date for API (YYYY-MM-DD)
 */
export function formatDateForApi(date: Date): string {
	return format(date, 'yyyy-MM-dd')
}

/**
 * Format time for display (HH:MM)
 */
export function formatTime(date: Date): string {
	return format(date, 'HH:mm')
}

/**
 * Parse date string from API (YYYY-MM-DD)
 */
export function parseDateFromApi(dateStr: string): Date {
	const [year, month, day] = dateStr.split('-').map(Number)
	return new Date(year, month - 1, day)
}

/**
 * Parse time string (HH:MM) and combine with date
 */
export function parseTimeToDate(date: Date, timeStr: string): Date {
	const [hours, minutes] = timeStr.split(':').map(Number)
	const result = new Date(date)
	result.setHours(hours, minutes, 0, 0)
	return result
}

/**
 * Get week number of the year
 */
export function getWeekNumber(date: Date): number {
	const firstDayOfYear = new Date(date.getFullYear(), 0, 1)
	const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000
	return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7)
}

/**
 * Get array of dates for a week
 */
export function getWeekDates(date: Date): Date[] {
	const start = startOfWeek(date, { weekStartsOn: 1 })
	return Array.from({ length: 7 }, (_, i) => addDays(start, i))
}

/**
 * Format month and year for header display
 */
export function formatMonthYear(date: Date): string {
	return formatDateCzech(date, 'LLLL yyyy')
}

/**
 * Calculate duration in minutes between two times
 */
export function calculateDuration(startTime: string, endTime: string): number {
	const [startH, startM] = startTime.split(':').map(Number)
	const [endH, endM] = endTime.split(':').map(Number)
	return (endH * 60 + endM) - (startH * 60 + startM)
}

/**
 * Add minutes to a time string and return new time string
 */
export function addMinutesToTime(timeStr: string, minutes: number): string {
	const [hours, mins] = timeStr.split(':').map(Number)
	const totalMinutes = hours * 60 + mins + minutes
	const newHours = Math.floor(totalMinutes / 60) % 24
	const newMins = totalMinutes % 60
	return `${String(newHours).padStart(2, '0')}:${String(newMins).padStart(2, '0')}`
}

/**
 * Custom day layout algorithm for react-big-calendar
 * Based on the legacy josenikaAPI calendar.js drawEvents algorithm
 */

import { isCancelledOnly } from '@/lib/constants'
import type { AppointmentType, CalendarEvent } from '@/types/calendar'

interface StyledEvent {
	event: CalendarEvent
	style: {
		top: number
		height: number
		width: number
		xOffset: number
	}
}

interface LayoutParams {
	events: CalendarEvent[]
	minimumStartDifference: number
	slotMetrics: {
		getRange: (start: Date, end: Date) => { top: number; height: number }
	}
	accessors: {
		start: (event: CalendarEvent) => Date
		end: (event: CalendarEvent) => Date
	}
}

/**
 * Check if event is specifically cancelled (gray), not no-show (red)
 */
function isEventCancelledOnly(event: CalendarEvent): boolean {
	return isCancelledOnly(event.resource?.background || null)
}

/**
 * Check if two events overlap in time
 */
function doEventsOverlap(
	event1Start: number,
	event1End: number,
	event2Start: number,
	event2End: number
): boolean {
	return event1Start < event2End && event1End > event2Start
}

// Cancelled cascade: the first card spans 50-100%, each further overlapping
// card starts one step to the right and also ends at 100%. The step shrinks
// for big groups so the last card keeps MIN_CANCELLED_WIDTH and nothing
// spills into the next day's column.
const CANCELLED_START = 50
const CANCELLED_STEP = 15
const MIN_CANCELLED_WIDTH = 20

/**
 * Positions gray (cancelled) events. Each gets the lowest slot not taken by
 * an overlapping, earlier event; every group of transitively overlapping
 * events shares one step size derived from its slot count.
 * `events` must be sorted by start (then longest first).
 */
function layoutCancelledCascade(
	events: { event: CalendarEvent; start: number; end: number }[],
): Map<CalendarEvent, { xOffset: number; width: number }> {
	const result = new Map<CalendarEvent, { xOffset: number; width: number }>()
	let group: { event: CalendarEvent; slot: number }[] = []
	// Per slot: end time of the event currently occupying it
	let slotEnds: number[] = []
	let groupEnd = -Infinity

	const flush = () => {
		const slots = slotEnds.length
		const step =
			slots > 1
				? Math.min(CANCELLED_STEP, (100 - CANCELLED_START - MIN_CANCELLED_WIDTH) / (slots - 1))
				: 0
		for (const { event, slot } of group) {
			const xOffset = CANCELLED_START + slot * step
			result.set(event, { xOffset, width: 100 - xOffset })
		}
		group = []
		slotEnds = []
	}

	for (const e of events) {
		if (e.start >= groupEnd) flush()
		let slot = slotEnds.findIndex((end) => end <= e.start)
		if (slot === -1) slot = slotEnds.length
		slotEnds[slot] = e.end
		group.push({ event: e.event, slot })
		groupEnd = Math.max(groupEnd, e.end)
	}
	flush()
	return result
}

/**
 * Core layout: positions a homogeneous set of events within a single 0-100% column.
 * The factory below wraps this to support a "ghost" half-column.
 */
function layoutEventSubset(
	events: CalendarEvent[],
	slotMetrics: LayoutParams['slotMetrics'],
	accessors: LayoutParams['accessors'],
): StyledEvent[] {
	if (!events.length) return []

	// Get time info for all events
	const eventsWithTime = events.map((event) => ({
		event,
		start: accessors.start(event).getTime(),
		end: accessors.end(event).getTime(),
		duration: accessors.end(event).getTime() - accessors.start(event).getTime(),
		isCancelledOnly: isEventCancelledOnly(event), // gray only, not red no-show
	}))

	// Sort events: non-cancelled first (they go behind), then cancelled
	// For cancelled: longer first (they appear more to the left)
	eventsWithTime.sort((a, b) => {
		// Non-cancelled first (processed first, they're behind)
		if (a.isCancelledOnly !== b.isCancelledOnly) {
			return a.isCancelledOnly ? 1 : -1
		}
		// Then by start time
		if (a.start !== b.start) {
			return a.start - b.start
		}
		// For cancelled: longer first (leftmost position)
		if (a.isCancelledOnly && b.isCancelledOnly) {
			return b.duration - a.duration
		}
		// For non-cancelled: longer first
		return b.duration - a.duration
	})

	const cancelledEvents = eventsWithTime.filter((e) => e.isCancelledOnly)
	const cancelledLayout = layoutCancelledCascade(cancelledEvents)
	const styledEvents: StyledEvent[] = []

	for (let i = 0; i < eventsWithTime.length; i++) {
		const current = eventsWithTime[i]
		const range = slotMetrics.getRange(
			accessors.start(current.event),
			accessors.end(current.event)
		)

		// Find all previous events that overlap with current
		const overlappingPrevious = eventsWithTime.slice(0, i).filter((prev) =>
			doEventsOverlap(current.start, current.end, prev.start, prev.end)
		)

		let width = 100
		let xOffset = 0

		if (current.isCancelledOnly) {
			// Cancelled (gray) events: cascade from the middle to the right edge
			const position = cancelledLayout.get(current.event)!
			width = position.width
			xOffset = position.xOffset
		} else {
			// Active and no-show (red) events split the space among the ones
			// overlapping before them. While a cancelled event overlaps, that
			// space is only the left half — the cascade (drawn on top) owns the
			// right half and would hide anything placed there.
			const area = cancelledEvents.some((c) =>
				doEventsOverlap(current.start, current.end, c.start, c.end),
			)
				? CANCELLED_START
				: 100
			const activeOverlaps = overlappingPrevious.filter((e) => !e.isCancelledOnly).length
			width = area / (activeOverlaps + 1)
			xOffset = activeOverlaps * width
		}

		styledEvents.push({
			event: current.event,
			style: {
				top: range.top,
				height: range.height,
				width,
				xOffset,
			},
		})
	}

	return styledEvents
}

/**
 * Build a day-layout algorithm for react-big-calendar.
 *
 * - When `ghostMode` is false, behaves like the original single-calendar layout.
 * - When `ghostMode` is true, events whose `resource.type` matches `calendarType`
 *   render in the left half (0-50%) and other-calendar "ghost" events render in
 *   the right half (50-100%). The internal cancelled/no-show stacking is
 *   preserved within each half.
 */
export function createDayLayoutAlgorithm(
	calendarType: AppointmentType,
	ghostMode: boolean,
) {
	return function dayLayoutAlgorithm({
		events,
		slotMetrics,
		accessors,
	}: LayoutParams): StyledEvent[] {
		if (!events.length) return []

		if (!ghostMode) {
			return layoutEventSubset(events, slotMetrics, accessors)
		}

		const main: CalendarEvent[] = []
		const ghost: CalendarEvent[] = []
		for (const event of events) {
			if (event.resource?.type === calendarType) main.push(event)
			else ghost.push(event)
		}

		const mainStyled = layoutEventSubset(main, slotMetrics, accessors)
		const ghostStyled = layoutEventSubset(ghost, slotMetrics, accessors)

		for (const s of mainStyled) {
			s.style.width *= 0.5
			s.style.xOffset *= 0.5
		}
		for (const s of ghostStyled) {
			s.style.width *= 0.5
			s.style.xOffset = 50 + s.style.xOffset * 0.5
		}

		return [...mainStyled, ...ghostStyled]
	}
}
